// ─────────────────────────────────────────────────────────────────────
// <EventCard /> — design vision spec
//
// Card has:
//   - Colored "art" tile (3:4 aspect)  — picks 1 of 5 category accents
//   - Eyebrow (category label, accent color)
//   - Serif title
//   - Venue + date line
//   - Foot: gold "From ₹X" price + small tag chip (filling fast / sold out)
//
// Category accents map by slug — see CATEGORY_ACCENT below.
// ─────────────────────────────────────────────────────────────────────

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatINR, formatEventDate } from '../../lib/format';
import { FLOWER_GLYPH } from '../../lib/taxonomy';   // r7.6.4
import { useThemeOptional } from '../../themes/ThemeContext';

// ─── Category → accent variant ────────────────────────────────────────
// Maps event.vertical or event.category to one of 5 visual accents.
// Keep stable so users learn the visual cue.
const CATEGORY_ACCENT = {
  // Verticals
  'live-events':      { variant: 'gold',  glyph: '✦' },
  'sports':           { variant: 'sage',  glyph: '◯' },
  'movies':           { variant: 'rose',  glyph: '▶' },
  'workshops':        { variant: 'plum',  glyph: '✎' },
  'attractions':      { variant: 'slate', glyph: '⌖' },
  'spiritual':        { variant: 'gold',  glyph: 'ॐ' },
  // Categories (sub-vertical)
  'comedy':           { variant: 'slate', glyph: 'M' },
  'concert':          { variant: 'gold',  glyph: '♪' },
  'theatre':          { variant: 'rose',  glyph: 'थ' },
  'classical':        { variant: 'gold',  glyph: 'त' },
  'devotional':       { variant: 'gold',  glyph: 'ॐ' },
  'kids':             { variant: 'sage',  glyph: '✿' },
  'edm':              { variant: 'plum',  glyph: '♬' },
  'rock':             { variant: 'rose',  glyph: '◢' },
  'cricket':          { variant: 'sage',  glyph: '◯' },
  'football':         { variant: 'sage',  glyph: '⬢' },
};

const VARIANT_LABELS = {
  gold:  'Bhakti fusion',
  slate: 'Stand-up',
  rose:  'Theatre',
  sage:  'Sports',
  plum:  'Workshop',
};

function pickVariant(event) {
  if (!event) return { variant: 'gold', glyph: '✦' };
  const key =
    (event.vertical && CATEGORY_ACCENT[event.vertical.toLowerCase()]) ||
    (event.category && CATEGORY_ACCENT[event.category.toLowerCase()]);
  if (key) return key;

  // Fallback: stable hash on event.id → pick from 5 variants
  const variants = ['gold', 'slate', 'rose', 'sage', 'plum'];
  let hash = 0;
  const s = String(event.id || event.slug || event.title || '');
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  const v = variants[Math.abs(hash) % variants.length];
  const glyph = (event.title || '✦').charAt(0).toUpperCase();
  return { variant: v, glyph };
}

// ─── Component ────────────────────────────────────────────────────────
export default function EventCard({ event, compact = false, className = '' }) {
  const { t } = useTranslation();
  // r7.8: both hooks run before any early return — `useTheme()` in a try/catch AFTER `if (!event)`
  // was a hook behind two conditionals at once.
  const mode = useThemeOptional()?.activeMode || 'dark';
  if (!event) return null;

  const picked = pickVariant(event);
  // r7.6.4: rows from the taxonomy carry their own colour + flower; those paint the tile and the eyebrow
  const catColour = event.category_slug ? (mode === 'light' ? (event.category_colour_light || event.category_colour) : (event.category_colour || event.category_colour_light)) : null;
  const variant = picked.variant;
  const glyph = event.category_slug && FLOWER_GLYPH[event.category_slug] ? FLOWER_GLYPH[event.category_slug] : picked.glyph;
  const eyebrow =
    event.category_label ||
    event.category ||
    VARIANT_LABELS[variant] ||
    'Event';

  const venue = event.venue_name || event.venue || '';
  const dateLabel = event.date ? formatEventDate(event.date) : '';
  const venueLine = [venue, dateLabel].filter(Boolean).join(' · ');

  const fromPrice = event.price_from ?? event.min_price ?? event.price_regular;
  const priceLabel = fromPrice != null
    ? `${t('common.from', 'From')} ${formatINR(fromPrice)}`
    : t('common.free', 'Free');

  // Tag (filling fast / sold out / limited / new)
  let tag = null;
  if (event.sales_stopped) {
    tag = { label: t('event.sales_stopped', 'sales stopped'), tone: 'muted' };
  } else if (event.available_seats === 0 || event.is_sold_out) {
    tag = { label: t('event.sold_out', 'sold out'), tone: 'danger' };
  } else if (event.total_seats && event.available_seats != null) {
    const ratio = event.available_seats / event.total_seats;
    if (ratio < 0.15) {
      tag = { label: t('event.filling_fast', 'filling fast'), tone: 'accent' };
    } else if (ratio < 0.4) {
      tag = { label: t('event.limited', 'limited'), tone: 'muted' };
    }
  }

  const href = `/e/${event.slug || event.id}`;

  return (
    <Link
      to={href}
      className={`snt-card group block no-underline text-text ${className}`}
      aria-label={event.title}
    >
      {/* Colored art tile */}
      <div
        className={`snt-art-${variant} aspect-[3/4] flex items-center justify-center font-serif overflow-hidden relative`}
        style={{ fontSize: compact ? 48 : 64, opacity: 0.6, ...(catColour ? { background: `linear-gradient(160deg, color-mix(in srgb, ${catColour} 38%, var(--snt-bg-2)) 0%, var(--snt-bg-2) 80%)`, color: catColour } : {}) }}
        data-category={event.category_slug || undefined}
      >
        {event.poster_url ? (
          <img
            src={event.poster_url}
            alt={event.title}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <span aria-hidden="true">{glyph}</span>
        )}

        {event.is_premiere && (
          <span
            className="absolute top-3 left-3 snt-eyebrow"
            style={{
              padding: '4px 10px',
              background: 'rgba(185,28,28,0.9)',
              color: '#FFF',
              borderRadius: 3,
              fontSize: 9,
            }}
          >
            Premieres
          </span>
        )}
      </div>

      {/* Meta */}
      <div className="px-4.5 py-4">
        <div
          className={`snt-micro mb-2`}
          style={{ color: catColour || `var(--snt-category-${variantToIdx(variant)})` }}
        >
          {eyebrow}
        </div>
        <div className="font-serif text-[17px] leading-tight text-text mb-2">
          {event.title}
        </div>
        {venueLine && (
          <div className="text-[11px] text-text-3 mb-3.5">{venueLine}</div>
        )}
        <div className="flex justify-between items-center pt-3.5 border-t border-line">
          <span className="text-xs text-accent">{priceLabel}</span>
          {tag && <Tag tone={tag.tone}>{tag.label}</Tag>}
        </div>
      </div>
    </Link>
  );
}

function variantToIdx(v) {
  return { gold: 1, slate: 2, rose: 3, sage: 4, plum: 5 }[v] || 1;
}

function Tag({ tone = 'muted', children }) {
  const toneStyle = {
    accent: { color: 'var(--snt-accent)', background: 'transparent' },
    danger: { color: 'var(--snt-danger)', background: 'transparent' },
    muted:  { color: 'var(--snt-text-3)', background: 'var(--snt-bg-2)' },
  }[tone] || {};
  return (
    <span
      className="text-[10px] px-2 py-1 rounded-full"
      style={toneStyle}
    >
      {children}
    </span>
  );
}
